import { Action } from "redux"

import * as actions from "./actions"
import { IItem } from "../../models"
import { ICardView, ICardViewItem } from "./index"

export const initialState: ICardView = {
    collection: {
        collectionId: 0,
        isSmartList: false,
    },
    items: [],
    paging: {
        currentPage: 1,
        pageSize: 0,
        totalNumberOfItems: 0,
    },
    allowAdd: false,
}

function addNullContextMenu(item: IItem): ICardViewItem {
    return { ...item, contextMenu: null }
}

const ACTION_HANDLERS: {
    [action: string]: (state: ICardView, props: Action) => ICardView
} = {
    [actions.INITIALIZE]: (
        state,
        { collection }: actions.Initialize,
    ): ICardView => ({
        ...state,
        collection,
    }),

    [actions.RECEIVE_DATA]: (
        state,
        { data: { items, paging, allowAdd } }: actions.CollectionViewDataAction,
    ): ICardView => {
        const cardviewItems = items.map(addNullContextMenu)

        return {
            ...state,
            allowAdd,
            paging,
            // if this is the first page we replace, otherwise we append
            items:
                paging.currentPage === 1
                    ? cardviewItems
                    : state.items.concat(...cardviewItems),
        }
    },

    [actions.REPLACE_DATA]: (
        state,
        { data: { items, paging } }: actions.CollectionViewDataAction,
    ): ICardView => {
        const cardviewItems = items.map(addNullContextMenu)

        return {
            ...state,
            paging,
            items: cardviewItems,
        }
    },

    [actions.LOAD_MORE]: (state): ICardView => ({
        ...state,
        paging: {
            ...state.paging,
            currentPage: state.paging.currentPage + 1,
        },
    }),

    [actions.REMOVE_ITEM]: (
        state,
        { itemId }: actions.ItemAction,
    ): ICardView => {
        const itemIndex = state.items.findIndex((i) => i.id === itemId)

        if (itemIndex === -1) {
            return state
        }

        const newItems = state.items.slice(0)
        newItems.splice(itemIndex, 1)

        return {
            ...state,
            items: newItems,
            paging: {
                ...state.paging,
                totalNumberOfItems: state.paging.totalNumberOfItems - 1,
            },
        }
    },

    [actions.RECEIVE_ITEMS]: (
        state,
        { items }: actions.ReceiveItemsAction,
    ): ICardView => {
        const newItems = state.items.slice(0)

        items.forEach((item) => {
            const itemIndex = state.items.findIndex((i) => i.id === item.id)
            if (itemIndex === -1) {
                return
            }
            newItems[itemIndex] = {
                ...newItems[itemIndex],
                ...item,
            }
        })

        return {
            ...state,
            items: newItems,
        }
    },

    [actions.HIDE_CONTEXT_MENU]: (
        state,
        { itemId }: actions.ItemAction,
    ): ICardView => {
        const { items } = state
        const itemIndex = items.findIndex((i) => i.id === itemId)

        if (itemIndex === -1) {
            return state
        }

        const newItems = Array.from(items)

        newItems[itemIndex] = {
            ...items[itemIndex],
            contextMenu: null,
        }

        return {
            ...state,
            items: newItems,
        }
    },

    [actions.RECEIVE_CONTEXTMENU_DATA]: (
        state,
        { itemId, buttons }: actions.ContextMenuAction,
    ): ICardView => {
        const { items } = state
        const itemIndex = items.findIndex((i) => i.id === itemId)

        if (itemIndex === -1) {
            return state
        }

        const newItems = Array.from(items)

        newItems[itemIndex] = {
            ...items[itemIndex],
            contextMenu: {
                actions: buttons,
                isShown: true,
                bookmarkButton: {
                    bookmarkButtonId: "CARDVIEW_BOOKMARK_BUTTON",
                    bookmarkId: null,
                    isLoading: true,
                    workspaceId: newItems[itemIndex]?.workspaceId ?? 0,
                    resourceType: "Item",
                    url: `${window.location.pathname}/${itemId}`,
                    resourceId: itemId,
                },
            },
        }

        return {
            ...state,
            items: newItems,
        }
    },

    [actions.RECEIVE_ATTACHMENTS]: (
        state,
        { itemId, linkedDocuments }: actions.ReceiveAttachments,
    ): ICardView => {
        const itemIndex = state.items.findIndex((item) => item.id === itemId)

        if (itemIndex < 0) {
            return state
        }

        const itemWithNewAttachments: ICardViewItem = {
            ...state.items[itemIndex],
            linkedDocuments,
        }

        const newItems = [...state.items]
        newItems[itemIndex] = itemWithNewAttachments

        return {
            ...state,
            items: newItems,
        }
    },
}

export default function reducer(
    state: ICardView = initialState,
    action: Action,
): ICardView {
    const handler: (state: ICardView, action: Action) => ICardView =
        ACTION_HANDLERS[action.type]
    return handler ? handler(state, action) : state
}
